import { useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { isToolsOnly, TOOLS_HOME, toolsRoleAllowsPath } from "@/auth/role";
import { Sidebar } from "@/shell/Sidebar";
import { GlobalSearch, useGlobalSearchHotkey } from "@/components/GlobalSearch";
import { CandidateModalHost } from "@/pages/Candidates/CandidateModalHost";
import { InboundCallHost } from "@/components/inboundCall/InboundCallHost";
import { OutgoingCallHost } from "@/components/outgoingCall/OutgoingCallHost";
import { ReminderToastHost } from "@/components/reminderToast/ReminderToastHost";
import { StageToastHost } from "@/components/stageToast/StageToastHost";
import { SupportHost } from "@/pages/Support/SupportWindow";
import "./shell.css";

// Свёрнутое меню помнится между заходами: иначе после перезагрузки оно
// раскрывалось снова, а на доске вакансии каждый раз ради ширины его прячут
// заново. Хранилище бывает закрыто (приватное окно, запрет) — тогда по-старому.
const SIDEBAR_COLLAPSED_KEY = "newats.sidebar.collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function AppShell() {
  const { state } = useAuth();
  const user = state.status === "authed" ? state.user : null;
  // Роль «Инструменты» — узкий доступ: одна вкладка и свой профиль. Сервер
  // остальное ей и так не отдаёт (ats_api.auth), здесь — чтобы она не
  // утыкалась в пустые экраны с ошибками, набрав адрес руками.
  const toolsOnly = isToolsOnly(user);
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* без хранилища меню просто не запомнится */
    }
  }, [collapsed]);
  // The search dialog lives here rather than in the sidebar so Ctrl+K works on
  // every page, including with the sidebar collapsed.
  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = useCallback(() => {
    // Ctrl+K у роли «Инструменты» не открывает ничего: искать ей нечего и не в
    // чем — сам диалог ниже тоже не смонтирован.
    if (!toolsOnly) setSearchOpen(true);
  }, [toolsOnly]);
  useGlobalSearchHotkey(openSearch);

  if (toolsOnly && !toolsRoleAllowsPath(pathname)) {
    return <Navigate to={TOOLS_HOME} replace />;
  }

  return (
    <div className={"shell" + (collapsed ? " shell--collapsed" : "")}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        onOpenSearch={openSearch}
      />
      <main className="shell-content">
        <Outlet />
      </main>
      {/* Всё, что ниже, живёт чужими данными: кандидатами, чатами, звонками.
          Роли «Инструменты» эти ручки закрыты (403), и дело не только в пустых
          экранах — звонки ей не приходят по замыслу, а не по недосмотру.
          Поэтому хосты не монтируются вовсе: смонтированные, они бы висели на
          закрытом потоке событий и переподключались к нему без конца. */}
      {!toolsOnly && (
        <>
          <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
          <CandidateModalHost />
          {/* Входящие звонки: всплывашки у всех вошедших и окно прослушки/перехвата.
              Слой --z-call — над карточкой кандидата и её ящиками. */}
          <InboundCallHost />
          {/* Исходящий звонок: окно и софтфон — здесь, а не в ленте или карточке,
              поэтому звонок переживает смену страницы; свёрнутая плашка встаёт в
              стопку входящих. */}
          <OutgoingCallHost />
          {/* Наступившие напоминания — только свои; стопка там же, слоем ниже звонков. */}
          <ReminderToastHost />
          {/* Смена этапа в моём чате, сделанная не мной, — внизу слева, над списком чатов. */}
          <StageToastHost />
          {/* Окно «Поддержка» / панель «Обращения» администратора — на весь экран поверх меню. */}
          <SupportHost />
        </>
      )}
    </div>
  );
}
